import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api';
import GateReportPanel from './GateReportPanel';

// ── Types ──────────────────────────────────────────────────────────────────────

interface GateItem {
  title: string;
  status: 'PASS' | 'FAIL' | 'PENDING' | 'WAIVED'; // FT-GATE.A2 — sync with API GateItem.status
  reason?: string;
  actionRequired?: string;
}

interface GateResult {
  projectId: string;
  phase: string;
  gateType: 'ENTRY' | 'EXIT';
  overallResult: 'PASS' | 'FAIL' | 'PASS_WITH_OVERRIDE';
  items: GateItem[];
  evaluatedAt: string;
  overridden?: boolean;
  overriddenAt?: string | null;
}

const PHASE_NAMES: Record<string, string> = {
  phase_1_requirements:    'Phase 1 — Requirements',
  phase_2_architecture:    'Phase 2 — Architecture',
  phase_2_5_ux_design:     'Phase 2.5 — UX Design',
  phase_3_sprint_planning: 'Phase 3 — Sprint Planning',
  phase_4_development:     'Phase 4 — Development',
  phase_5_code_review:     'Phase 5 — Code Review',
  phase_6_testing:         'Phase 6 — QA/Testing',
  phase_7_deployment:      'Phase 7 — Deployment',
};

// 25 mirrors PHASE_MAP in routes/phase-transitions.ts — URL params are
// digits-only so 2.5 is encoded as 25.
const PHASE_NUMBERS: Record<string, number> = {
  phase_1_requirements:    1,
  phase_2_architecture:    2,
  phase_2_5_ux_design:    25,
  phase_3_sprint_planning: 3,
  phase_4_development:     4,
  phase_5_code_review:     5,
  phase_6_testing:         6,
  phase_7_deployment:      7,
};

// ── GateStatusBanner ───────────────────────────────────────────────────────────

interface Props {
  projectId: string;
  currentPhase: string;
  canOverride?: boolean;
  onOverrideSuccess?: () => void;
}

export default function GateStatusBanner({ projectId, currentPhase, canOverride, onOverrideSuccess }: Props) {
  const [gateResult, setGateResult] = useState<GateResult | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const phaseNumber = PHASE_NUMBERS[currentPhase];

  useEffect(() => {
    if (!phaseNumber) return;
    apiFetch<GateResult>(`/projects/${projectId}/phases/${phaseNumber}/gate/entry`)
      .then(setGateResult)
      .catch(() => {/* silent */});
  }, [projectId, phaseNumber]);

  if (!gateResult || gateResult.overallResult === 'PASS') return null;

  const failCount = gateResult.items.filter(i => i.status === 'FAIL').length;
  const phaseName = PHASE_NAMES[currentPhase] ?? currentPhase;

  const isOverride = gateResult.overallResult === 'PASS_WITH_OVERRIDE' || !!gateResult.overridden;

  return (
    <>
      <div style={{
        background: 'rgba(245,158,11,0.08)',
        border: '1px solid rgba(245,158,11,0.3)',
        borderRadius: 'var(--radius-sm)',
        padding: '10px 14px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>⚠</span>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--warning)' }}>
            {isOverride ? (
              <><strong>{phaseName}</strong> entry gate: passed with override</>
            ) : (
              <><strong>{phaseName}</strong> entry gate: {failCount} item{failCount !== 1 ? 's' : ''} require attention</>
            )}
          </span>
        </div>
        <button
          onClick={() => setPanelOpen(true)}
          style={{
            background: 'none',
            border: '1px solid rgba(245,158,11,0.4)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--warning)',
            fontFamily: 'var(--font-body)',
            fontSize: 12, fontWeight: 600,
            padding: '4px 10px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          View Details
        </button>
      </div>

      {phaseNumber && (
        <GateReportPanel
          projectId={projectId}
          phaseNumber={phaseNumber}
          gateType="entry"
          isOpen={panelOpen}
          onClose={() => setPanelOpen(false)}
          canOverride={canOverride}
          onOverrideSuccess={() => {
            setPanelOpen(false);
            setGateResult(null);
            onOverrideSuccess?.();
          }}
        />
      )}
    </>
  );
}
